import React, { FC } from 'react';
import {
  Box,
  Button,
  Container,
  Typography,
  Paper,
  Alert,
} from '@mui/material';
import MonitorHeartIcon from '@mui/icons-material/MonitorHeart';
import SyncIcon from '@mui/icons-material/Sync';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import SecurityIcon from '@mui/icons-material/Security';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import { BRAND, BRAND_DARK, BRAND_DARKER, BG } from './healthWalletConstants';
import { healthWalletApi } from './healthWalletSlice';
import { useAppDispatch } from '../../app/hooks';
import { DataRequestSSEUpdates } from './DataRequestSSEUpdates';

interface WalletLoadingScreenProps {
  onContinue: () => void;
}

export const WalletLoadingScreen: FC<WalletLoadingScreenProps> = ({ onContinue }) => {
  //const [data, setData] = useState<any>(null);
  //const [isLoading, setIsLoading] = useState(false);
  //const [isSuccess, setIsSuccess] = useState(false);
  //const [isError, setIsError] = useState(false);

  // 🆕 Add state for SSE restart functionality
  //const [sseKey, setSseKey] = useState(0); // Key to force remount

  const dispatch = useAppDispatch();

  // NO mount-fire (2026-07-12, Leo): the batched-retrieval trigger moved to the connect screen's
  // "Get my records" button (OnboardingProviders.handleGetRecords) — the mount effect here re-fired
  // the trigger on every reload/remount of this screen against an already-processing run. This screen
  // is now DISPLAY-ONLY: it shows progress (SSE) for the run the button started.

  // Handle continue button click
  const handleContinue = () => {
    // Optimistic update: mark as onboarded to trigger navigation
    dispatch(
      healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
        draft.isOnboarded = true;
        //draft.isOnboardedForced = true;
      })
    );

    onContinue();
  };

  // // Optional: Update loading states based on mutation status
  // useEffect(() => {
  //   setIsLoading(isMutationLoading);
  //   setIsSuccess(isMutationSuccess);
  //   if (isMutationError) {
  //     setIsError(true);
  //     console.error('[WalletLoadingScreen] Mutation error:', mutationError);
  //   }
  // }, [isMutationLoading, isMutationSuccess, isMutationError, mutationError]);

  // Status handlers
  // const handleLoadingChange = (loading: boolean) => {
  //   setIsLoading(loading);
  // };

  // const handleSuccessChange = (success: boolean) => {
  //   setIsSuccess(success);

  //   if (success) {
  //     // 🔄 Invalidate cache when SSE completes successfully
  //     console.log('[WalletLoadingScreen] SSE completed successfully, invalidating cache...');
  //     dispatch(healthWalletApi.util.invalidateTags(['Patient']));
  //   }
  // };

  // const handleErrorChange = (error: boolean) => {
  //   setIsError(error);

  //   if (error) {
  //     // Update onboarding state with error using RTK Query dispatch
  //     dispatch(
  //       healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
  //         //draft.dataRequestStatus = 'failed';
  //         draft.error = 'Failed to retrieve health records';
  //         draft.browserTrusted = false; // Reset browser trust on error
  //       })
  //     );
  //   }
  // };

  // 🆕 Restart workflow function
  // const handleRestartWorkflow = async() => {
  //   console.log('[WalletLoadingScreen] Restarting workflow...');
  //   setSseKey(prev => prev + 1); // Force remount with new key
  //    try {
  //       console.log('[WalletLoadingScreen] Triggering health data request...');
  //       const result = await requestHealthData().unwrap();
  //       console.log('[WalletLoadingScreen] Data request initiated:', result);
  //       // Optimistic update: Merge entire returned state
  //       if (result?.state) {
  //         console.log('[WalletLoadingScreen] Optimistic update: merging returned state into onboarding status');
  //         dispatch(
  //           healthWalletApi.util.updateQueryData('getOnboardingStatus', undefined, (draft) => {
  //             Object.assign(draft, result.state); // Merge entire returned state
  //           })
  //         );
  //       }
  //     } catch (error) {
  //       console.error('[WalletLoadingScreen] Failed to request health data:', error);
  //       //setIsError(true);
  //     }
  // };

  return (
    <Box sx={{ bgcolor: BG, minHeight: '100svh', width: '100%', display: 'flex', flexDirection: 'column', mt: { xs: '-56px', sm: '-64px' } }}>
        {/* Header (flat — design cue: Overview) */}
        <Box component="header" sx={{
          pt: { xs: '88px', sm: '112px' }, pb: { xs: 4, md: 5 }, width: '100%',
        }}>
          <Container maxWidth="sm">
            <Box sx={{ textAlign: 'center' }}>
              <Box sx={{
                width: 80, height: 80, borderRadius: '50%', mx: 'auto', mb: 2.5,
                bgcolor: BRAND,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                lineHeight: 0,
              }}>
                <SyncIcon sx={{ color: BRAND_DARKER, fontSize: 40, display: 'block' }} />
              </Box>
              <Typography variant="h5" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1 }}>
                Retrieving Your Health Records
              </Typography>
              <Typography variant="body1" sx={{ color: '#6b6b70', mb: 3 }}>
                We're securely requesting your medical records from healthcare providers nationwide
              </Typography>
            </Box>
          </Container>
        </Box>

        {/* Info card */}
        <Container component="main" maxWidth="sm" sx={{ mt: -3, pb: 6, position: 'relative', zIndex: 1 }}>
          <Paper elevation={3} sx={{ borderRadius: 4, overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.08)' }}>
            <Box sx={{ p: { xs: 3, md: 4 } }}>

              {/* What's happening */}
              <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f0f9ff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <MonitorHeartIcon sx={{ color: '#0284c7', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1 }}>
                    What's happening now?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    We're sending secure FHIR requests through the Qualified Health Information Network (QHIN) to retrieve your medical records from hospitals, clinics, and providers across the country.
                  </Typography>
                </Box>
              </Box>

              {/* How long */}
              <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <AccessTimeIcon sx={{ color: '#d97706', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1 }}>
                    How long will this take?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    This process typically takes <strong>2 to 10 minutes</strong> depending on how many providers have records for you. Some records may continue arriving over the next few hours.
                  </Typography>
                </Box>
              </Box>

              {/* Security note */}
              <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                <Box sx={{ width: 56, height: 56, borderRadius: '50%', bgcolor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <SecurityIcon sx={{ color: '#16a34a', fontSize: 32 }} />
                </Box>
                <Box>
                  <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#1c1c1e', mb: 1 }}>
                    Your data is secure
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                    All records are encrypted end-to-end. Only you can access your Health Wallet through your verified CLEAR identity.
                  </Typography>
                </Box>
              </Box>

              <Alert severity="info" sx={{ borderRadius: 2, mb: 2, '& .MuiAlert-icon': { color: BRAND_DARKER } }}>
                You don't need to wait on this page. Feel free to continue to your wallet in the meantime — records will appear as they arrive.
              </Alert>

              {/* Continue Button with Restart Option */}
              <Box sx={{ mt: 4, textAlign: 'center' }}>
                {/* 🆕 Show Restart Button on Success */}

                <Button
                  variant="contained"
                  size="large"
                  onClick={handleContinue}
                  sx={{
                    minWidth: 200,
                    py: 1.5,
                    bgcolor: BRAND_DARKER,
                    borderRadius: 50,
                    textTransform: 'none',
                    fontWeight: 700,
                    '&:hover': { bgcolor: '#5a3fc0' },
                  }}
                >
                  Ok
                </Button>
              </Box>
            </Box>
          </Paper>

        </Container>
      </Box>
  );
};
